import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { compressImage } from "../utils/imageCompression";
import { useCompressionSettings } from "../utils/CompressionContext";
import { ReferenceImageGallery } from "../components/finetuning/ReferenceImageGallery";
import { ReferenceImageUpload } from "../components/finetuning/ReferenceImageUpload";
import { ImagePreviewDialog } from "../components/finetuning/ImagePreviewDialog";
import { Brain, Images, Upload, Loader2, Package, ListChecks, CheckCircle, ChevronRight, History } from "lucide-react";

export default function FinetuningPage() {
  const { token } = useAuth();
  const compressionSettings = useCompressionSettings();
  const refImageInputRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [steps, setSteps] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedStep, setSelectedStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSteps, setLoadingSteps] = useState(false);

  const [referenceImages, setReferenceImages] = useState([]);
  const [loadingRefImages, setLoadingRefImages] = useState(false);
  const [uploadingNewRef, setUploadingNewRef] = useState(false);
  const [newRefImage, setNewRefImage] = useState({
    label: "",
    description: "",
    is_positive: true,
    image_base64: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState("gallery");
  const [refHistory, setRefHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`, { headers });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchSteps = useCallback(async (productId) => {
    if (!productId) return;
    setLoadingSteps(true);
    try {
      const response = await axios.get(`${API}/steps?product_id=${productId}`, { headers });
      const stepsData = response.data;
      // Fetch reference image counts for each step
      const countsPromises = stepsData.map(s =>
        axios.get(`${API}/steps/${s.id}/reference-images`, { headers }).then(r => r.data.length).catch(() => 0)
      );
      const counts = await Promise.all(countsPromises);
      stepsData.forEach((s, i) => { s._ref_count = counts[i]; });
      setSteps(stepsData);
    } catch (error) {
      toast.error("Failed to load steps");
    } finally {
      setLoadingSteps(false);
    }
  }, [headers]);

  const fetchReferenceImages = useCallback(async (step) => {
    if (!step) return;
    setLoadingRefImages(true);
    try {
      const response = await axios.get(`${API}/steps/${step.id}/reference-images`, { headers });
      setReferenceImages(response.data);
    } catch (error) {
      toast.error("Failed to load reference images");
      setReferenceImages([]);
    } finally {
      setLoadingRefImages(false);
    }
  }, [headers]);

  const fetchRefHistory = useCallback(async (step) => {
    if (!step) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/steps/${step.id}/reference-history`, { headers });
      setRefHistory(response.data);
    } catch { setRefHistory([]); }
    finally { setLoadingHistory(false); }
  }, [headers]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    if (selectedProduct) {
      fetchSteps(selectedProduct);
      setSelectedStep(null);
      setReferenceImages([]);
    }
  }, [selectedProduct, fetchSteps]);

  useEffect(() => {
    if (selectedStep) {
      fetchReferenceImages(selectedStep);
      fetchRefHistory(selectedStep);
      setActiveTab("gallery");
    }
  }, [selectedStep, fetchReferenceImages, fetchRefHistory]);

  const handleRefImageFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPEG, PNG, or WEBP)");
      return;
    }
    try {
      toast.loading("Compressing image...", { id: "compress-ref" });
      const compressedBase64 = await compressImage(file, {
        maxWidth: compressionSettings.maxWidth,
        maxHeight: compressionSettings.maxHeight,
        quality: compressionSettings.quality,
        maxSizeKB: compressionSettings.maxSizeKB
      });
      setNewRefImage({ ...newRefImage, image_base64: compressedBase64 });
      toast.success("Image compressed!", { id: "compress-ref" });
    } catch (error) {
      toast.error("Failed to process image", { id: "compress-ref" });
    }
  };

const handleAddReferenceImage = async () => {
    if (!newRefImage.label.trim()) { toast.error("Label is required"); return; }
    if (!newRefImage.image_base64) { toast.error("Please select an image"); return; }

    setUploadingNewRef(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("label", newRefImage.label);
      formDataToSend.append("description", newRefImage.description || "");
      formDataToSend.append("is_positive", newRefImage.is_positive);

      const byteCharacters = atob(newRefImage.image_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/jpeg" });
      formDataToSend.append("image", blob, `${newRefImage.label}.jpg`);

      await axios.post(
        `${API}/steps/${selectedStep.id}/reference-images`,
        formDataToSend,
        { headers }
      );

      toast.success("Reference image added");
      fetchReferenceImages(selectedStep);
      setNewRefImage({ label: "", description: "", is_positive: true, image_base64: null });
      if (refImageInputRef.current) refImageInputRef.current.value = "";
      setActiveTab("gallery");
    } catch (error) {
      toast.error("Failed to add reference image");
    } finally {
      setUploadingNewRef(false);
    }
  };

  const handleDeleteReferenceImage = async (imageId) => {
    if (!window.confirm("Delete this reference image?")) return;
    try {
      await axios.delete(`${API}/steps/${selectedStep.id}/reference-images/${imageId}`, { headers });
      toast.success("Reference image deleted");
      setReferenceImages(referenceImages.filter(img => img.id !== imageId));
    } catch (error) {
      toast.error("Failed to delete reference image");
    }
  };

  const handleBulkRefUpload = async (e, labelPrefix = "reference", isPositive = true, description = "") => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validFiles = files.filter(f => f.type.startsWith("image/"));
    if (validFiles.length === 0) { toast.error("Please select valid image files"); return; }

    setUploadingNewRef(true);
    try {
      toast.loading(`Compressing ${validFiles.length} images...`, { id: "bulk-compress" });
      const compressedFiles = await Promise.all(
        validFiles.map(async (file) => {
          const compressedBase64 = await compressImage(file, {
            maxWidth: compressionSettings.maxWidth,
            maxHeight: compressionSettings.maxHeight,
            quality: compressionSettings.quality,
            maxSizeKB: compressionSettings.maxSizeKB
          });
          const byteCharacters = atob(compressedBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          return new Blob([byteArray], { type: "image/jpeg" });
        })
      );

      const formDataToSend = new FormData();
      compressedFiles.forEach((blob, index) => {
        formDataToSend.append("images", blob, `image_${index}.jpg`);
      });
      formDataToSend.append("label_prefix", labelPrefix);
      formDataToSend.append("is_positive", isPositive);
      formDataToSend.append("description", description);

      toast.loading("Uploading...", { id: "bulk-compress" });
      await axios.post(
        `${API}/steps/${selectedStep.id}/finetune`,
        formDataToSend,
        { headers }
      );

      toast.success(`${validFiles.length} images uploaded`, { id: "bulk-compress" });
      fetchReferenceImages(selectedStep);
      setActiveTab("gallery");
    } catch (error) {
      toast.error("Failed to upload images", { id: "bulk-compress" });
    } finally {
      setUploadingNewRef(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const selectedProductName = products.find(p => p.id === selectedProduct)?.name;

  return (
    <div className="space-y-6" data-testid="finetuning-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="w-7 h-7 text-blue-500" />
            AI Finetuning
          </h2>
          <p className="text-muted-foreground">
            Upload reference images to train the AI for better verification accuracy
          </p>
        </div>
      </div>

      {/* Step selectors — compact horizontal flow */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</div>
                Product
              </label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger>
                  <Package className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Select product..." />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <ChevronRight className="hidden md:block w-5 h-5 text-muted-foreground mb-2" />

            <div className="flex-1 space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${selectedProduct ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>2</div>
                Verification Step
              </label>
              {!selectedProduct ? (
                <div className="h-10 flex items-center text-sm text-muted-foreground">Select a product first</div>
              ) : loadingSteps ? (
                <div className="h-10 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              ) : (
                <Select value={selectedStep?.id || ""} onValueChange={(id) => setSelectedStep(steps.find(s => s.id === id))}>
                  <SelectTrigger>
                    <ListChecks className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Select step..." />
                  </SelectTrigger>
                  <SelectContent>
                    {steps.map((step) => (
                      <SelectItem key={step.id} value={step.id}>
                        <span className="flex items-center gap-2">
                          {step.step_number}. {step.name}
                          <span className={`text-xs ${step._ref_count > 0 ? "text-emerald-600" : "text-red-500"}`}>
                            ({step._ref_count || 0} refs)
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedStep && (
              <>
                <ChevronRight className="hidden md:block w-5 h-5 text-muted-foreground mb-2" />
                <div className="flex items-end gap-2 mb-0.5">
                  <Badge variant="outline" className="h-8">
                    {selectedStep.cv_task}
                  </Badge>
                  <Badge variant="secondary" className="h-8">
                    <Images className="w-3 h-3 mr-1" />
                    {referenceImages.length} images
                  </Badge>
                </div>
              </>
            )}
          </div>

          {selectedProduct && selectedStep && (
            <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-green-600">
              <CheckCircle className="w-3.5 h-3.5" />
              {selectedProductName} &gt; Step {selectedStep.step_number}: {selectedStep.name}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reference images section */}
      {selectedStep && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Reference Images
                  <Badge variant="secondary" className="ml-1 font-normal">
                    {referenceImages.length}
                  </Badge>
                </CardTitle>
                <CardDescription className="mt-1">
                  Positive examples = correct state. Negative examples = defects to reject.
                </CardDescription>
              </div>
              <Button
                size="sm"
                onClick={() => setActiveTab("upload")}
                className={activeTab === "upload" ? "hidden" : ""}
              >
                <Upload className="w-4 h-4 mr-2" />
                Add Images
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="gallery">
                  <Images className="w-4 h-4 mr-2" />
                  Gallery
                </TabsTrigger>
                <TabsTrigger value="upload">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger value="history">
                  <History className="w-4 h-4 mr-2" />
                  History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="gallery" className="mt-4">
                <ReferenceImageGallery
                  referenceImages={referenceImages}
                  loadingRefImages={loadingRefImages}
                  onPreview={setPreviewImage}
                  onDelete={handleDeleteReferenceImage}
                  headers={headers}
                />
              </TabsContent>

              <TabsContent value="upload" className="mt-4">
                <ReferenceImageUpload
                  newRefImage={newRefImage}
                  setNewRefImage={setNewRefImage}
                  refImageInputRef={refImageInputRef}
                  uploadingNewRef={uploadingNewRef}
                  onFileSelect={handleRefImageFileSelect}
                  onAddImage={handleAddReferenceImage}
                  onBulkUpload={handleBulkRefUpload}
                />
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                {loadingHistory ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : refHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No history yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {refHistory.map((h, i) => (
                      <div key={h.id || i} className={`flex items-start gap-3 p-3 rounded-lg border ${h.action === "added" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                        <div className={`w-2 h-2 rounded-full mt-2 ${h.action === "added" ? "bg-emerald-500" : "bg-red-500"}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">{h.action === "added" ? "Added" : "Removed"}</span>
                            <span className="text-muted-foreground">"{h.label}"</span>
                            {h.is_positive_example !== undefined && (
                              <span className={`text-xs ${h.is_positive_example ? "text-emerald-600" : "text-red-600"}`}>
                                ({h.is_positive_example ? "positive" : "negative"})
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            by {h.user_name} &bull; {new Date(h.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <ImagePreviewDialog
        previewImage={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
}
